import { get, writable } from "svelte/store";
import chronology, { dayNumber } from "./chronology.js";
import locality from "./locality.js";
import weather from "./weather.js";
import persisted from "./common/persisted.js";
import profile from "./profile.js";
import { createJournalEntry } from "../api/api.js";

function createJournalStore() {
	const store = writable({
		all: [],
		memories: [],
		indexForCurrent: undefined,
		isLoading: false
	});

	const { set, subscribe, update } = store;

	async function updateJournal() {
		const $locality = get(locality);
		const $chronology = get(chronology);
		const $profile = get(profile);
		const $weather = get(weather);
		const $journal = get(journal);
		const day = dayNumber($chronology);

		// Ensure all required data exists before proceeding. Check objects before accessing their properties.
		if (!$locality || !$profile || !$profile.color || !$profile.displayName || !$weather || !$weather.formatted) {
			// Data not ready, silently return instead of throwing error
			return;
		}

		const randomMemories = $journal.memories.sort(() => (Math.random() < 0.5 ? -1 : 1)).slice(0, 3);

		const memories =
			$journal.memories.length === 0
				? "Nothing interesting is happening. "
				: `Here are some recent events I remember: ${randomMemories.join(", ")}. `;

		update((state) => ({
			...state,
			all: [
				...state.all,
				{
					day,
					locality: $locality,
					note: `Writing a note...`
				}
			],
			memories: [],
			isLoading: true
		}));

		try {
			const { entry } = await createJournalEntry(
				day,
				$locality,
				$profile.color,
				$profile.displayName.toLowerCase(),
				$weather.formatted,
				memories
			);

			if (typeof entry !== "string") {
				throw new Error("failed to write a journal entry");
			}

			update((state) => ({
				...state,
				all: [
					...state.all.slice(0, -1),
					{
						...state.all.at(-1),
						note: entry
					}
				],
				indexForCurrent: state.all.length - 1,
				isLoading: false
			}));
		} catch (error) {
			update((state) => ({
				...state,
				all: [...state.all.slice(0, -1)],
				isLoading: false
			}));

			console.warn(error);
		}
	}

	function shouldUpdate() {
		const $locality = get(locality);
		if (!$locality) return false;

		const $journal = get(store);
		if ($journal.isLoading) return false;

		const $chronology = get(chronology);
		if (Date.now() - $chronology.startedAt < 10_000) return false;

		if ($journal.all.length === 0) return true;

		return $journal.all.at(-1).day !== dayNumber($chronology);
	}

	setInterval(function didTriggerInterval() {
		if (shouldUpdate()) {
			updateJournal();
		}
	}, 5_000);

	if (shouldUpdate()) {
		updateJournal();
	}

	return {
		set,
		subscribe,

		previous() {
			update((state) => ({
				...state,
				indexForCurrent: Math.max(state.indexForCurrent - 1, 0)
			}));
		},

		next() {
			update((state) => ({
				...state,
				indexForCurrent: Math.min(state.indexForCurrent + 1, state.all.length - 1)
			}));
		},

		pause() {
			update((state) => ({
				...state,
				isPaused: true
			}));
		},

		resume() {
			update((state) => ({
				...state,
				isPaused: false
			}));
		},

		remember(memory) {
			update((state) => ({
				...state,
				memories: [...state.memories, memory]
			}));
		}
	};
}

let journal;

if (process.env.NODE_ENV === "production") {
	journal = persisted("journal:v5", true, createJournalStore());
} else {
	journal = createJournalStore();
}

export default journal;
